import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { generateCSRFToken, validateCSRFToken } from '../utils/securityUtils';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 50px 20px;
  min-height: 80vh;
  background-color: #fff;
`;

const FormWrapper = styled.div`
  width: 100%;
  max-width: 420px;
  padding: 40px;
  background-color: #ffffff;
  border: 1px solid #000;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const Title = styled.h1`
  font-family: 'CursiveNice', sans-serif;
  font-size: 32px;
  font-weight: normal;
  margin-bottom: 10px;
  color: #000;
  letter-spacing: 1.5px;
`;

const Subtitle = styled.p`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', sans-serif;
  font-size: 16px;
  color: #333;
  margin-bottom: 30px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px;
  margin-bottom: 20px;
  font-size: 16px;
  border: 1px solid #000;
  border-radius: 4px;
  outline: none;
  transition: border-color 0.2s;
  background-color: #fff;

  &:focus {
    border-color: #543230;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px;
  font-size: 16px;
  font-weight: bold;
  color: #ffffff;
  background-color: #000000;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 10px;

  &:hover {
    background-color: #543230;
  }

  &:disabled {
    background-color: #9e9e9e;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: #d32f2f;
  font-size: 14px;
  margin-bottom: 15px;
  text-align: left;
`;

const StyledLink = styled(Link)`
  margin-top: 20px;
  font-size: 14px;
  color: #333;
  text-decoration: none;

  &:hover {
    color: #543230;
    text-decoration: underline;
  }
`;

const SignUp = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');
  const { signup } = useAuth();
  const navigate = useNavigate();
  
  // Generate CSRF token when component mounts
  useEffect(() => {
    setCsrfToken(generateCSRFToken());
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate CSRF token
    const formToken = e.target.elements.csrf_token.value;
    if (!validateCSRFToken(formToken)) {
      setError('Security validation failed. Please refresh the page and try again.');
      return;
    }

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    try {
      setError('');
      setLoading(true);
      await signup(email, password, firstName, lastName);
      navigate('/'); // Redirect to home page after successful signup
    } catch (err) {
      setError(err.message || 'Failed to create an account. Please try again.');
      console.error(err);
    }

    setLoading(false);
  };

  return (
    <Container>
      <FormWrapper>
        <Title>Create Your Account</Title>
        <Subtitle>Join the Sprezzatura family.</Subtitle>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <Form onSubmit={handleSubmit}>
          <input 
            type="hidden" 
            name="csrf_token" 
            value={csrfToken} 
          />
          <Input
            type="text"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
          <Input
            type="text"
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
          <Input
            type="email"
            placeholder="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <Input
            type="password"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          <Button disabled={loading} type="submit">
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </Form>
        <StyledLink to="/login">Already have an account? Sign In</StyledLink>
      </FormWrapper>
    </Container>
  );
};

export default SignUp;
